/**
 * ReviewPanel — IA-agnostic composition of the review_agent sidecar's
 * output for a single (session, module) target.
 *
 * ONE component. Both IAs (classic tabbed UI + chat-first card feed)
 * host it with a single ``<ReviewPanel sessionId=... moduleId=... />``
 * call. Zero composition logic duplicated across IAs.
 *
 * Why this exists (Agentic Coding Principles + Anti-Dark-Code):
 *   - SRP: this component owns "how to assemble + render review data."
 *     Hosts own "where to place the panel." Two responsibilities,
 *     two owners, cleanly separated.
 *   - DRY: the hook call + error handling + card composition live here,
 *     not duplicated across every container.
 *   - OCP: adding a third IA is a one-line host call — no edit here.
 *   - DIP: hosts depend on the props contract, not on cards / hook /
 *     service internals.
 *
 * Spec: docs/specs/10-review-agent-sidecar.md § HTTP surface.
 */

import { Button, Caption1, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { ChevronDownRegular, ChevronUpRegular, InfoRegular } from "@fluentui/react-icons";
import type React from "react";
import { useState } from "react";
import { useReviewAgent } from "../../hooks/useReviewAgent";
import { type ReviewProgressStep, useReviewProgress } from "../../hooks/useReviewProgress";
import { PersonaScorecardCard } from "./PersonaScorecardCard";
import { SubtaskCard } from "./SubtaskCard";
import type { PersonaIssue } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  collapsedHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: "pointer",
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
  },
  errorBanner: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorderActive}`,
    color: tokens.colorNeutralForeground1,
  },
  progress: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  progressHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
  },
  progressSteps: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
  },
});

/** Human-readable label for one streamed key step. */
function describeStep(step: ReviewProgressStep): string {
  const d = step.data;
  const name = typeof d.persona_name === "string" ? d.persona_name : "reviewer";
  switch (step.event) {
    case "review_started": {
      const count = typeof d.count === "number" ? d.count : undefined;
      return count != null
        ? `Reviewing with ${count} persona${count === 1 ? "" : "s"}…`
        : "Starting review…";
    }
    case "persona_started":
      return `Reviewing — ${name}…`;
    case "persona_scored": {
      const verdict = typeof d.verdict === "string" ? d.verdict : null;
      if (d.errored) return `${name}: errored`;
      return verdict ? `${name}: ${verdict}` : `${name}: scored`;
    }
    case "review_complete":
      return "Report ready";
    case "review_error":
      return "Review failed";
    default:
      return step.event;
  }
}

/** Live key-step list shown while a section's report is being produced. */
const ProgressSteps: React.FC<{ steps: ReviewProgressStep[] }> = ({ steps }) => {
  const styles = useStyles();
  if (steps.length === 0) return null;
  return (
    <div className={styles.progress} aria-live="polite">
      <div className={styles.progressHeader}>
        <Spinner size="extra-tiny" />
        <Caption1>Preparing review…</Caption1>
      </div>
      <ul className={styles.progressSteps}>
        {steps.map((s) => (
          <li key={`${s.seq}-${s.event}`}>
            <Caption1>{describeStep(s)}</Caption1>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface ReviewPanelProps {
  /** Sidecar session id, e.g. ``"DEMO_CSR__demo_session"``. */
  sessionId: string;
  /** Module id, e.g. ``"CSR-04"`` — or the sidecar sentinels
   *  ``"_document"`` / ``"_finalization"`` for run-level scopes. */
  moduleId: string;
  /** When true, the panel starts collapsed with a summary header.
   *  Chat-first hosts typically pass true (cards are dense in a feed);
   *  classic tab hosts usually leave it false. */
  defaultCollapsed?: boolean;
  /** Optional issue-click handler. Classic hosts typically wire this
   *  to scroll-to-excerpt in Word; chat-first hosts might open a
   *  details dialog. If omitted, issues render as non-interactive. */
  onIssueClick?: (issue: PersonaIssue) => void;
}

export const ReviewPanel: React.FC<ReviewPanelProps> = ({
  sessionId,
  moduleId,
  defaultCollapsed = false,
  onIssueClick,
}) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  const { audit, review, loading, error, notYetProduced } = useReviewAgent({
    session: sessionId,
    target: moduleId,
  });

  // Live per-step progress while the report is being produced. Only stream
  // when the final record isn't in yet — once ``audit`` exists the poll owns
  // the display. Replaces the single static "Review pending" line.
  const { steps: progressSteps } = useReviewProgress({
    session: sessionId,
    target: moduleId,
    enabled: !audit,
  });

  // Collapsed header — a one-line summary the writer clicks to expand.
  if (!expanded) {
    const summary = audit
      ? `${audit.module_id} · ${audit.aggregated_verdict}`
      : notYetProduced
        ? "Review pending"
        : "Review";
    return (
      <button
        type="button"
        className={styles.collapsedHeader}
        onClick={() => setExpanded(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded(true);
          }
        }}
        aria-label={`Expand review panel for ${moduleId}`}
      >
        <Text weight="semibold">{summary}</Text>
        <ChevronDownRegular />
      </button>
    );
  }

  // --- Expanded states --------------------------------------------------

  if (error) {
    return (
      <div className={styles.errorBanner} aria-live="polite">
        <Text weight="semibold">Review unavailable</Text>
        <Caption1>{error.message}</Caption1>
        <Caption1>
          Check that the review_agent sidecar is running on
          <code> http://localhost:8001</code> (or
          <code> VITE_REVIEW_AGENT_BASE</code>).
        </Caption1>
      </div>
    );
  }

  if (notYetProduced) {
    // Stream the live key steps if a review is in flight; otherwise the
    // original "check back" hint.
    if (progressSteps.length > 0) {
      return <ProgressSteps steps={progressSteps} />;
    }
    return (
      <div className={styles.infoBanner} aria-live="polite">
        <InfoRegular />
        <Caption1>
          No review produced yet for <code>{moduleId}</code>. Runs are processed post-draft; check back after the module
          completes.
        </Caption1>
      </div>
    );
  }

  if (!audit) {
    if (progressSteps.length > 0) {
      return <ProgressSteps steps={progressSteps} />;
    }
    return (
      <div className={styles.status} aria-live="polite">
        <Spinner size="extra-tiny" />
        <Caption1>Loading review…</Caption1>
      </div>
    );
  }

  // Happy path — render subtask list + per-persona scorecards
  const scorecards = review?.scorecards ?? [];

  return (
    <div className={styles.root}>
      {defaultCollapsed && (
        <button
          type="button"
          className={styles.collapsedHeader}
          onClick={() => setExpanded(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(false);
            }
          }}
          aria-label={`Collapse review panel for ${moduleId}`}
        >
          <Text weight="semibold">
            {audit.module_id} · {audit.aggregated_verdict}
          </Text>
          <ChevronUpRegular />
        </button>
      )}

      <SubtaskCard
        moduleId={audit.module_id}
        subtasks={audit.subtasks}
        parseStatus={audit.parse_status}
        parseErrors={audit.parse_errors}
      />

      {scorecards.length === 0 ? (
        <div className={styles.infoBanner}>
          <InfoRegular />
          <Caption1>
            No persona scorecards yet. If the audit shows <code>personas_run: 0</code>, no persona was applicable at
            this scope/doc_type; otherwise the scorecards file is still being written.
          </Caption1>
        </div>
      ) : (
        scorecards.map((sc) => <PersonaScorecardCard key={sc.persona_id} scorecard={sc} onIssueClick={onIssueClick} />)
      )}

      {loading && (
        <div className={styles.status}>
          <Spinner size="extra-tiny" />
          <Caption1>Refreshing…</Caption1>
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;
